import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { 
    getAdminSettings, 
    updateAdminSettings,
    getDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    getCategories,
    getAvailableStaff
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    ArrowLeft, 
    Settings, 
    Key,
    Mail,
    Building,
    Users,
    Plus,
    Pencil,
    Trash2,
    Loader2,
    Save,
    Eye,
    EyeOff,
    Tags
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettingsPage() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // API Settings
    const [settings, setSettings] = useState({
        brevo_api_key: '',
        openapi_token: '',
        notification_email: '',
        email_from: '',
        email_from_name: ''
    });
    const [showApiKeys, setShowApiKeys] = useState(false);
    
    // Departments
    const [departments, setDepartments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [staff, setStaff] = useState([]);
    
    // Department Dialog
    const [deptDialogOpen, setDeptDialogOpen] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [deptForm, setDeptForm] = useState({
        nome: '',
        descrizione: '',
        categorie: [],
        staff: []
    });
    const [deleteDeptDialogOpen, setDeleteDeptDialogOpen] = useState(false);
    const [deptToDelete, setDeptToDelete] = useState(null);
    const [deptSubmitting, setDeptSubmitting] = useState(false);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/operator');
            return;
        }
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [settingsRes, deptsRes, catsRes, staffRes] = await Promise.all([
                getAdminSettings(),
                getDepartments(),
                getCategories(),
                getAvailableStaff()
            ]);
            
            setSettings(settingsRes.data);
            setDepartments(deptsRes.data);
            setCategories(catsRes.data);
            setStaff(staffRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsChange = (e) => {
        setSettings({ ...settings, [e.target.name]: e.target.value });
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await updateAdminSettings(settings);
            toast.success('Impostazioni salvate');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    // Department functions
    const openNewDeptDialog = () => {
        setEditingDept(null);
        setDeptForm({
            nome: '',
            descrizione: '',
            categorie: [],
            staff: []
        });
        setDeptDialogOpen(true);
    };

    const openEditDeptDialog = (dept) => {
        setEditingDept(dept);
        setDeptForm({
            nome: dept.nome,
            descrizione: dept.descrizione || '',
            categorie: dept.categorie || [],
            staff: dept.staff || []
        });
        setDeptDialogOpen(true);
    };

    const handleDeptFormChange = (e) => {
        setDeptForm({ ...deptForm, [e.target.name]: e.target.value });
    };

    const toggleCategory = (category) => {
        const current = [...deptForm.categorie];
        const index = current.indexOf(category);
        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(category);
        }
        setDeptForm({ ...deptForm, categorie: current });
    };

    const toggleStaff = (staffId) => {
        const current = [...deptForm.staff];
        const index = current.indexOf(staffId);
        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(staffId);
        }
        setDeptForm({ ...deptForm, staff: current });
    };

    const handleSaveDept = async () => {
        if (!deptForm.nome.trim()) {
            toast.error('Nome reparto richiesto');
            return;
        }

        setDeptSubmitting(true);
        try {
            if (editingDept) {
                await updateDepartment(editingDept.id, deptForm);
                toast.success('Reparto aggiornato');
            } else {
                await createDepartment(deptForm);
                toast.success('Reparto creato');
            }
            setDeptDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setDeptSubmitting(false);
        }
    };

    const handleDeleteDept = async () => {
        if (!deptToDelete) return;
        
        setDeptSubmitting(true);
        try {
            await deleteDepartment(deptToDelete.id);
            toast.success('Reparto eliminato');
            setDeleteDeptDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
        } finally {
            setDeptSubmitting(false);
        }
    };

    const getStaffName = (staffId) => {
        const member = staff.find(s => s.id === staffId);
        return member ? `${member.nome} ${member.cognome}` : staffId;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/operator')}
                                className="text-slate-600"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                            <div className="flex items-center gap-2">
                                <Settings className="w-5 h-5 text-indigo-500" />
                                <h1 className="text-lg font-semibold text-slate-900">Impostazioni Sistema</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Tabs defaultValue="api" className="space-y-6">
                    <TabsList className="bg-white border">
                        <TabsTrigger value="api" className="gap-2">
                            <Key className="w-4 h-4" />
                            API & Email
                        </TabsTrigger>
                        <TabsTrigger value="departments" className="gap-2">
                            <Building className="w-4 h-4" />
                            Reparti
                        </TabsTrigger>
                    </TabsList>

                    {/* API Settings Tab */}
                    <TabsContent value="api">
                        <div className="space-y-6">
                            {/* API Keys */}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <Key className="w-5 h-5" />
                                                Chiavi API
                                            </CardTitle>
                                            <CardDescription>
                                                Configura le chiavi API per i servizi esterni
                                            </CardDescription>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowApiKeys(!showApiKeys)}
                                        >
                                            {showApiKeys ? (
                                                <><EyeOff className="w-4 h-4 mr-2" /> Nascondi</>
                                            ) : (
                                                <><Eye className="w-4 h-4 mr-2" /> Mostra</>
                                            )}
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <Label htmlFor="brevo_api_key">Brevo API Key</Label>
                                        <Input
                                            id="brevo_api_key"
                                            name="brevo_api_key"
                                            type={showApiKeys ? "text" : "password"}
                                            value={settings.brevo_api_key}
                                            onChange={handleSettingsChange}
                                            placeholder="xkeysib-..."
                                            className="mt-1 font-mono text-sm"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">
                                            Usata per l'invio delle email transazionali
                                        </p>
                                    </div>

                                    <div>
                                        <Label htmlFor="openapi_token">OpenAPI.it Token</Label>
                                        <Input
                                            id="openapi_token"
                                            name="openapi_token"
                                            type={showApiKeys ? "text" : "password"}
                                            value={settings.openapi_token}
                                            onChange={handleSettingsChange}
                                            placeholder="Token..."
                                            className="mt-1 font-mono text-sm"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">
                                            Usata per la ricerca dati aziendali tramite P.IVA
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Email Settings */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Mail className="w-5 h-5" />
                                        Impostazioni Email
                                    </CardTitle>
                                    <CardDescription>
                                        Configura le email di sistema e le notifiche
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="email_from">Email Mittente</Label>
                                            <Input
                                                id="email_from"
                                                name="email_from"
                                                type="email"
                                                value={settings.email_from}
                                                onChange={handleSettingsChange}
                                                placeholder="noreply@acm-service.srl"
                                                className="mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="email_from_name">Nome Mittente</Label>
                                            <Input
                                                id="email_from_name"
                                                name="email_from_name"
                                                value={settings.email_from_name}
                                                onChange={handleSettingsChange}
                                                placeholder="Sistema Ticket"
                                                className="mt-1"
                                            />
                                        </div>
                                    </div>

                                    <Separator />

                                    <div>
                                        <Label htmlFor="notification_email">Email Notifiche Nuovi Ticket</Label>
                                        <Input
                                            id="notification_email"
                                            name="notification_email"
                                            type="email"
                                            value={settings.notification_email}
                                            onChange={handleSettingsChange}
                                            placeholder="assistenza@acm-service.srl"
                                            className="mt-1"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">
                                            Riceverai una notifica a questo indirizzo quando viene aperto un nuovo ticket
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex justify-end">
                                <Button 
                                    onClick={handleSaveSettings}
                                    disabled={saving}
                                    className="bg-indigo-500 hover:bg-indigo-600"
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Salva Impostazioni
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Departments Tab */}
                    <TabsContent value="departments">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Building className="w-5 h-5" />
                                            Gestione Reparti
                                        </CardTitle>
                                        <CardDescription>
                                            Crea reparti e assegna categorie e staff
                                        </CardDescription>
                                    </div>
                                    <Button
                                        onClick={openNewDeptDialog}
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nuovo Reparto
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {departments.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <Building className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                        <p>Nessun reparto configurato</p>
                                        <p className="text-sm">Crea un reparto per organizzare le categorie e lo staff</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {departments.map((dept) => (
                                            <div 
                                                key={dept.id}
                                                className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h3 className="font-semibold text-slate-900">{dept.nome}</h3>
                                                        {dept.descrizione && (
                                                            <p className="text-sm text-slate-500 mt-1">{dept.descrizione}</p>
                                                        )}
                                                        
                                                        {/* Categories */}
                                                        <div className="mt-3">
                                                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                                                <Tags className="w-3 h-3" />
                                                                Categorie:
                                                            </div>
                                                            <div className="flex flex-wrap gap-1">
                                                                {dept.categorie && dept.categorie.length > 0 ? (
                                                                    dept.categorie.map((cat, idx) => (
                                                                        <Badge key={idx} variant="secondary" className="text-xs">
                                                                            {cat}
                                                                        </Badge>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-xs text-slate-400">Nessuna categoria</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Staff */}
                                                        <div className="mt-3">
                                                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                                                <Users className="w-3 h-3" />
                                                                Staff:
                                                            </div>
                                                            <div className="flex flex-wrap gap-1">
                                                                {dept.staff && dept.staff.length > 0 ? (
                                                                    dept.staff.map((staffId, idx) => (
                                                                        <Badge key={idx} variant="outline" className="text-xs">
                                                                            {getStaffName(staffId)}
                                                                        </Badge>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-xs text-slate-400">Nessuno staff assegnato</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-1 ml-4">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditDeptDialog(dept)}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => {
                                                                setDeptToDelete(dept);
                                                                setDeleteDeptDialogOpen(true);
                                                            }}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>

            {/* Department Dialog */}
            <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingDept ? 'Modifica Reparto' : 'Nuovo Reparto'}
                        </DialogTitle>
                        <DialogDescription>
                            Configura il reparto, le categorie associate e lo staff
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="dept-nome">Nome Reparto *</Label>
                            <Input
                                id="dept-nome"
                                name="nome"
                                value={deptForm.nome}
                                onChange={handleDeptFormChange}
                                placeholder="Es: Supporto Tecnico"
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label htmlFor="dept-desc">Descrizione</Label>
                            <Textarea
                                id="dept-desc"
                                name="descrizione"
                                value={deptForm.descrizione}
                                onChange={handleDeptFormChange}
                                placeholder="Descrizione del reparto..."
                                rows={2}
                                className="mt-1"
                            />
                        </div>

                        <Separator />

                        {/* Categories Selection */}
                        <div>
                            <Label className="mb-3 block">Categorie Associate</Label>
                            <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                                {categories.map((cat, idx) => (
                                    <div key={idx} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`cat-${idx}`}
                                            checked={deptForm.categorie.includes(cat)}
                                            onCheckedChange={() => toggleCategory(cat)}
                                        />
                                        <Label 
                                            htmlFor={`cat-${idx}`} 
                                            className="text-sm font-normal cursor-pointer"
                                        >
                                            {cat}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* Staff Selection */}
                        <div>
                            <Label className="mb-3 block">Staff Assegnato</Label>
                            <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                                {staff.map((member) => (
                                    <div key={member.id} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`staff-${member.id}`}
                                            checked={deptForm.staff.includes(member.id)}
                                            onCheckedChange={() => toggleStaff(member.id)}
                                        />
                                        <Label 
                                            htmlFor={`staff-${member.id}`} 
                                            className="text-sm font-normal cursor-pointer flex items-center gap-2"
                                        >
                                            {member.nome} {member.cognome}
                                            <Badge variant="outline" className="text-xs">
                                                {member.ruolo}
                                            </Badge>
                                        </Label>
                                    </div>
                                ))}
                                {staff.length === 0 && (
                                    <p className="text-sm text-slate-500">Nessuno staff disponibile</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeptDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleSaveDept}
                            disabled={deptSubmitting}
                            className="bg-indigo-500 hover:bg-indigo-600"
                        >
                            {deptSubmitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            {editingDept ? 'Salva' : 'Crea'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Department Dialog */}
            <AlertDialog open={deleteDeptDialogOpen} onOpenChange={setDeleteDeptDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questo reparto?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Stai per eliminare il reparto "<strong>{deptToDelete?.nome}</strong>". 
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteDept}
                            className="bg-red-500 hover:bg-red-600"
                            disabled={deptSubmitting}
                        >
                            {deptSubmitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
